import { computed, Injectable, signal } from "@angular/core";
import { Notification, NotificationFilter } from "@models/notification";
import { NotificationPage } from "./notification.service";

/**
 * Store em memória (signals) do centro de notificações.
 * Mantido em sincronia com o REST (carga/paginação) e com o
 * NotificationsChannel (eventos em tempo real).
 */
@Injectable({
  providedIn: "root",
})
export class NotificationStore {
  private readonly _notifications = signal<Notification[]>([]);
  private readonly _unreadCount = signal<number>(0);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<boolean>(false);
  private readonly _filter = signal<NotificationFilter>("all");
  private readonly _currentPage = signal<number>(1);
  private readonly _totalPages = signal<number>(0);
  private readonly _totalCount = signal<number>(0);
  private readonly _pageSize = signal<number>(20);

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly filter = this._filter.asReadonly();
  readonly currentPage = this._currentPage.asReadonly();
  readonly totalPages = this._totalPages.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  readonly pageSize = this._pageSize.asReadonly();

  readonly hasMore = computed(() => this._currentPage() < this._totalPages());
  readonly isEmpty = computed(
    () => !this._loading() && this._notifications().length === 0,
  );

  setLoading(loading: boolean): void {
    this._loading.set(loading);
    if (loading) {
      this._error.set(false);
    }
  }

  setError(error: boolean): void {
    this._error.set(error);
    this._loading.set(false);
  }

  /** Aplica uma página vinda do REST. `append` acumula (scroll/paginação). */
  setFromList(result: NotificationPage, append = false): void {
    const incoming = result.page.data;
    this._notifications.set(
      append ? [...this._notifications(), ...incoming] : incoming,
    );
    this._unreadCount.set(result.unreadCount);
    this._currentPage.set(result.page.meta.current_page);
    this._totalPages.set(result.page.meta.total_pages);
    this._totalCount.set(result.page.meta.total_count);
    this._pageSize.set(result.page.meta.page_size ?? 20);
    this._loading.set(false);
    this._error.set(false);
  }

  setUnreadCount(count: number): void {
    this._unreadCount.set(count);
  }

  /** Nova notificação em tempo real: insere no topo e incrementa não lidas. */
  prepend(notification: Notification): void {
    if (this._notifications().some((n) => n.id === notification.id)) {
      return;
    }
    this._notifications.set([notification, ...this._notifications()]);
    if (!notification.read_at) {
      this.incrementUnread();
    }
  }

  incrementUnread(): void {
    this._unreadCount.update((count) => count + 1);
  }

  zeroUnread(): void {
    this._unreadCount.set(0);
    this._notifications.update((list) =>
      list.map((n) =>
        n.read_at ? n : { ...n, read_at: new Date().toISOString() },
      ),
    );
  }

  /** Marca uma notificação como lida localmente e decrementa o contador. */
  markReadLocally(id: number): void {
    let wasUnread = false;
    this._notifications.update((list) =>
      list.map((n) => {
        if (n.id === id && !n.read_at) {
          wasUnread = true;
          return { ...n, read_at: new Date().toISOString() };
        }
        return n;
      }),
    );
    if (wasUnread) {
      this._unreadCount.update((count) => Math.max(0, count - 1));
    }
  }

  setFilter(filter: NotificationFilter): void {
    this._filter.set(filter);
  }

  reset(): void {
    this._notifications.set([]);
    this._unreadCount.set(0);
    this._loading.set(false);
    this._error.set(false);
    this._filter.set("all");
    this._currentPage.set(1);
    this._totalPages.set(0);
    this._totalCount.set(0);
    this._pageSize.set(20);
  }
}
